/**
 * <nosto-dynamic-card> — custom element override
 *
 * Defines the element BEFORE Nosto's bundle gets a chance to. Custom
 * element names can only be registered once; first define wins, so this
 * file replaces the default `@nosto/web-components` implementation with
 * one tuned for our Horizon integration:
 *
 *   • Fetch `{Shopify.routes.root}products/{handle}?section_id=card-product-nosto`
 *   • Concurrency queue: 5 in-flight on mobile, 10 on desktop
 *   • In-memory cache by handle (single Map shared across instances)
 *   • Viewport-priority ordering when `lazy` mode is on
 *   • 429 / 5xx → exponential backoff retry, max 3 attempts
 *   • 404      → element gets hidden, parent `<li>` hidden
 *   • Preserves outer height during refetch to prevent CLS
 *
 * Loaded synchronously in <head> BEFORE `content_for_header` so that
 * any `customElements.define('nosto-dynamic-card', ...)` call from the
 * Nosto bundle later is a no-op (we keep ownership of the element).
 */
(function () {
  if (typeof customElements === 'undefined') return;
  if (customElements.get('nosto-dynamic-card')) return;

  const cache = new Map();
  const pending = new Map();
  const queue = [];
  let running = 0;
  let rateLimitedUntil = 0;

  const isMobile = () => window.matchMedia('(max-width: 749px)').matches;
  const maxConcurrency = () => (isMobile() ? 5 : 10);

  function pump() {
    while (running < maxConcurrency() && queue.length) {
      queue.sort((a, b) => b.priority - a.priority);
      const task = queue.shift();
      running++;
      Promise.resolve()
        .then(task.run)
        .finally(() => {
          running--;
          pump();
        });
    }
  }

  function schedule(priority, run) {
    queue.push({ priority, run });
    pump();
  }

  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  async function fetchSection(handle, attempt = 0) {
    const now = Date.now();
    if (now < rateLimitedUntil) await sleep(rateLimitedUntil - now);

    // Préfixe de locale OBLIGATOIRE (`Shopify.routes.root` = `/` ou `/de/`, `/it/`…).
    // Sans lui, la Section Rendering API rend la carte dans la locale PAR DÉFAUT du
    // marché : les liens produit qu'elle contient perdent le segment de langue, donc
    // cliquer une carte sortait le visiteur de sa locale (de.nutrimuscle.com/de →
    // de.nutrimuscle.com). Le libellé et le prix de la carte étaient faux au passage.
    // Même résolution que `fetchNostoCard` dans `nosto-reco-core.js`.
    const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    const sectionUrl = new URL(`${root}products/${encodeURIComponent(handle)}`, window.location.origin);
    sectionUrl.searchParams.set('section_id', 'card-product-nosto');

    // Propagate the current theme-preview params so the card is rendered by the
    // SAME theme as the page. Without this, the Section Rendering fetch hits the
    // PUBLISHED theme — so card-level changes (made on a preview/dev theme) never
    // show, even though the page itself renders from the previewed theme.
    const pageParams = new URLSearchParams(window.location.search);
    for (const key of ['preview_theme_id', '_fd', 'pb', '_ab']) {
      const value = pageParams.get(key);
      if (value !== null) sectionUrl.searchParams.set(key, value);
    }

    const url = sectionUrl.toString();
    const resp = await fetch(url, { credentials: 'same-origin' });

    if (resp.status === 429 || resp.status >= 500) {
      rateLimitedUntil = Date.now() + 3000;
      if (attempt < 3) {
        await sleep(Math.min(2000 * Math.pow(2, attempt), 15000));
        return fetchSection(handle, attempt + 1);
      }
      throw new Error('rate-limited');
    }
    if (resp.status === 404) {
      const err = new Error('404');
      err.code = 404;
      throw err;
    }
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    const html = await resp.text();
    if (/^<!doctype/i.test(html.trim())) {
      throw new Error('section endpoint returned a full page');
    }
    return html;
  }

  function fetchOnce(handle) {
    const hit = cache.get(handle);
    if (hit) return Promise.resolve(hit);
    const inFlight = pending.get(handle);
    if (inFlight) return inFlight;
    const p = fetchSection(handle).then(
      (html) => {
        cache.set(handle, html);
        pending.delete(handle);
        return html;
      },
      (err) => {
        pending.delete(handle);
        throw err;
      }
    );
    pending.set(handle, p);
    return p;
  }

  class NostoDynamicCard extends HTMLElement {
    /** @type {boolean} Monté une fois — connectedCallback peut être rappelé (morph, move). */
    _mounted = false;
    /** @type {string} Dernier handle effectivement rendu (garde anti re-fetch). */
    _renderedHandle = '';
    /** @type {IntersectionObserver | null} Observer du fetch différé, une instance à la fois. */
    _observer = null;

    static get observedAttributes() {
      return ['handle', 'section', 'variant-id', 'lazy'];
    }

    get handle() {
      return this.getAttribute('handle') || '';
    }
    set handle(v) {
      v ? this.setAttribute('handle', v) : this.removeAttribute('handle');
    }
    get section() {
      return this.getAttribute('section') || '';
    }
    set section(v) {
      v ? this.setAttribute('section', v) : this.removeAttribute('section');
    }
    get lazy() {
      return this.hasAttribute('lazy');
    }
    set lazy(v) {
      v ? this.setAttribute('lazy', '') : this.removeAttribute('lazy');
    }

    connectedCallback() {
      if (this._mounted) return;
      this._mounted = true;
      if (!this.innerHTML.trim()) this._renderSkeleton();
      if (this.lazy) this._observeViewport();
      else this._render();
    }

    _renderSkeleton() {
      this.innerHTML =
        '<div class="product-card-wrapper" aria-hidden="true">' +
        '<div class="nosto-skeleton__cell"></div>' +
        '<div class="nosto-skeleton__line nosto-skeleton__line--desc"></div>' +
        '<div class="nosto-skeleton__line nosto-skeleton__line--title"></div>' +
        '<div class="nosto-skeleton__line nosto-skeleton__line--short"></div>' +
        '<div class="nosto-skeleton__line nosto-skeleton__line--price"></div>' +
        '</div>';
    }

    disconnectedCallback() {
      if (this._observer) {
        this._observer.disconnect();
        this._observer = null;
      }
      const rect = this.getBoundingClientRect();
      if (rect.height > 0) this.style.minHeight = `${rect.height}px`;
    }

    attributeChangedCallback(name, oldVal, newVal) {
      if (oldVal === newVal) return;
      if (this._mounted && (name === 'handle' || name === 'section')) {
        this._render();
      }
    }

    _observeViewport() {
      this._observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              this._observer.disconnect();
              this._observer = null;
              this._render();
              return;
            }
          }
        },
        { rootMargin: isMobile() ? '800px' : '1500px' }
      );
      this._observer.observe(this);
    }

    async _render() {
      const handle = this.handle;
      if (!handle) return;
      if (this._renderedHandle === handle) return;

      const rect = this.getBoundingClientRect();
      const priority =
        (rect.top < window.innerHeight ? 100 : 0) +
        Math.max(0, 50 - Math.abs(rect.top) / 100);

      schedule(priority, () =>
        fetchOnce(handle).then(
          (html) => {
            if (!this.isConnected) return;
            // Guard: if the section returns empty / missing markup (e.g. a
            // block-instance lookup failed server-side), leave the host's
            // existing content alone so the Preact default card stays
            // visible. Avoids visibly broken empty cards in production.
            const looksLikeCard =
              /<(ax-)?product-card[\s>]/i.test(html) ||
              /class="[^"]*(?:product-card-wrapper|ax-product-card)\b/.test(html);
            if (!looksLikeCard) {
              console.warn('[NostoDynamicCard] section returned no card markup for handle:', handle);
              this.dispatchEvent(
                new CustomEvent('nosto-card:invalid', {
                  bubbles: true,
                  detail: { handle, htmlSample: html.slice(0, 200) },
                })
              );
              return;
            }
            this.innerHTML = html;
            this._renderedHandle = handle;
            this.style.minHeight = '';
            // Tell the theme's lazy-skeleton + observer-CFH watchers to
            // scan the freshly-injected DOM. Without this, primary
            // images stay at opacity: 0 because `.cfh-loaded` is only
            // added on DOMContentLoaded (for elements present at boot)
            // or when `reactivateLazyObserver` fires. The same event
            // also re-arms observer-CFH for any lazy-src descendants.
            document.dispatchEvent(new CustomEvent('reactivateLazyObserver'));
            this.dispatchEvent(
              new CustomEvent('nosto-card:rendered', {
                bubbles: true,
                detail: { handle },
              })
            );
          },
          (err) => {
            if (!this.isConnected) return;
            if (err && err.code === 404) {
              this.style.display = 'none';
              const li = this.closest('li.grid__item');
              if (li) li.style.display = 'none';
            }
            this.dispatchEvent(
              new CustomEvent('nosto-card:error', {
                bubbles: true,
                detail: { handle, error: String(err) },
              })
            );
          }
        )
      );
    }
  }

  customElements.define('nosto-dynamic-card', NostoDynamicCard);

  window.NostoDynamicCard = NostoDynamicCard;
  window.NostoDynamicCard.prefetch = (handles) => {
    for (const h of handles || []) {
      schedule(0, () => fetchOnce(h).catch(() => {}));
    }
  };
})();
